import { stockInStockWasEntered } from './stockOnHand'
import { findVenueProductForStockTakeProduct } from './stockTakeConversions'
import {
  displayParLevelForStockLine,
  venueProductForStockTakeLine,
} from './stockTakeLinePar'

export const ORDER_TO_PAR_REVIEW_HINT =
  'Biggest gap first. Changing or removing a suggestion leaves the stock take as it was.'

export const ORDER_TO_PAR_COUNT_HINT =
  'Count In Stock, then Save changes. Order the gap from counted lines only.'

export const ORDER_TO_PAR_ADDED_TO_BASKET =
  'Added to basket. The stock take is unchanged.'

export const ORDER_TO_PAR_ADD_TO_BASKET = 'Add to basket'
export const ORDER_TO_PAR_ADD_TO_LIST_AND_BASKET =
  'Add to shopping list and basket'

export const orderToParAddToBasketLabel = (rows = []) => {
  const remaining = (Array.isArray(rows) ? rows : []).filter(
    item => (Number(item?.suggestedOrder) || 0) > 0,
  )
  if (remaining.some(item => item?.needsShoppingList)) {
    return ORDER_TO_PAR_ADD_TO_LIST_AND_BASKET
  }
  return ORDER_TO_PAR_ADD_TO_BASKET
}

export const ORDER_TO_PAR_ALREADY_IN_BASKET =
  'Nothing to add. Those lines are already in the basket or on open orders.'

export const ORDER_TO_PAR_INACTIVE_SKIPPED_HINT =
  'Inactive shopping-list products are left out — reactivate them on the shopping list to order again.'

export const countInactiveShoppingListSkipped = (skipped = []) =>
  (Array.isArray(skipped) ? skipped : []).filter(
    item => item?.reason === 'inactive_shopping_list',
  ).length

export const ORDER_TO_PAR_EMPTY_STATES = Object.freeze({
  NOT_COUNTED: 'not_counted',
  NO_PAR: 'no_par',
  PAR_ONLY_ON_UNCOUNTED: 'par_only_on_uncounted',
  NOTHING_BELOW_PAR: 'nothing_below_par',
  ALL_REMOVED: 'all_removed',
})

export const ORDER_TO_PAR_SET_PAR_CTA = 'Set par on counted lines'

export const STOCK_TAKE_PAR_FOCUS_ROW_CLASS = 'stock-take-par-focus-row'

export const ORDER_TO_PAR_EMPTY_COPY = Object.freeze({
  [ORDER_TO_PAR_EMPTY_STATES.NOT_COUNTED]:
    'Nothing to order yet. Count the lines first — suggestions only use counted stock.',
  [ORDER_TO_PAR_EMPTY_STATES.NO_PAR]:
    "Nothing to order. These counted lines don't say how much to keep.",
  [ORDER_TO_PAR_EMPTY_STATES.PAR_ONLY_ON_UNCOUNTED]:
    'Nothing to order. How much to keep is set on uncounted lines on this take, not the ones you counted.',
  [ORDER_TO_PAR_EMPTY_STATES.NOTHING_BELOW_PAR]:
    'Nothing to order. Counted stock is enough after basket and open orders.',
  [ORDER_TO_PAR_EMPTY_STATES.ALL_REMOVED]:
    'No suggestions left to order. The stock take is unchanged.',
})

const NOT_COUNTED_REASON = 'not_counted'
const NO_PAR_REASON = 'no_par'

/**
 * Empty review: uncounted lines, no pars, or nothing below par.
 * Priority: nobody counted → no par on counted lines → nothing below par.
 */
export const pickOrderToParEmptyState = ({
  recommendations = [],
  skipped = [],
  reviewRows = [],
} = {}) => {
  const recs = Array.isArray(recommendations) ? recommendations : []
  const rows = Array.isArray(reviewRows) ? reviewRows : recs
  const remaining = rows.filter(item => (Number(item?.suggestedOrder) || 0) > 0)
  if (remaining.length) return null
  if (recs.length) return ORDER_TO_PAR_EMPTY_STATES.ALL_REMOVED

  const skippedList = Array.isArray(skipped) ? skipped : []
  const counted = skippedList.filter(
    item => item?.reason !== NOT_COUNTED_REASON,
  )
  if (!counted.length) return ORDER_TO_PAR_EMPTY_STATES.NOT_COUNTED

  const countedWithPar = counted.filter(item => item?.reason !== NO_PAR_REASON)
  if (!countedWithPar.length) {
    const parOnlyOnUncounted = skippedList.some(
      item => item?.reason === NOT_COUNTED_REASON && Number(item?.parLevel) > 0,
    )
    return parOnlyOnUncounted
      ? ORDER_TO_PAR_EMPTY_STATES.PAR_ONLY_ON_UNCOUNTED
      : ORDER_TO_PAR_EMPTY_STATES.NO_PAR
  }

  return ORDER_TO_PAR_EMPTY_STATES.NOTHING_BELOW_PAR
}

export const formatOrderToParNoParCopy = () =>
  ORDER_TO_PAR_EMPTY_COPY[ORDER_TO_PAR_EMPTY_STATES.NO_PAR]

export const orderToParEmptyCopy = emptyState =>
  ORDER_TO_PAR_EMPTY_COPY[emptyState] || null

export const orderToParShowsSetParCta = emptyState =>
  emptyState === ORDER_TO_PAR_EMPTY_STATES.NO_PAR ||
  emptyState === ORDER_TO_PAR_EMPTY_STATES.PAR_ONLY_ON_UNCOUNTED

/** Add to basket only while remaining rows are from a saved take. */
export const canAddOrderToParToBasket = ({
  isLocked = false,
  loaded = false,
  emptyState = null,
  belowParCount = 0,
  hasUnsavedChanges = false,
} = {}) =>
  !isLocked &&
  !hasUnsavedChanges &&
  Boolean(loaded) &&
  !emptyState &&
  Number(belowParCount) > 0

const asMatchId = value => {
  if (value == null || value === '') return ''
  if (typeof value === 'object')
    return String(value._id || value.id || '').trim()
  return String(value).trim()
}

const normalizeTitleSupplier = (title, supplierTitle) =>
  `${String(title || '')
    .trim()
    .toLowerCase()}|${String(supplierTitle || '')
    .trim()
    .toLowerCase()}`

/** Venue products with par that are not the counted lines on this take. */
export const listOtherProductsWithPar = ({
  lines = [],
  venueProducts = [],
} = {}) => {
  const counted = (Array.isArray(lines) ? lines : []).filter(
    stockInStockWasEntered,
  )
  const countedVenueIds = new Set(
    counted.map(line => asMatchId(line?.venueProductId)).filter(Boolean),
  )
  const countedProductIds = new Set(
    counted.map(line => asMatchId(line?.productId)).filter(Boolean),
  )
  const countedKeys = new Set(
    counted.map(line =>
      normalizeTitleSupplier(line?.title, line?.supplierTitle),
    ),
  )

  return (Array.isArray(venueProducts) ? venueProducts : []).filter(product => {
    const par = Number(product?.parLevel)
    if (!Number.isFinite(par) || par <= 0) return false
    const venueProductId = asMatchId(product?._id)
    if (venueProductId && countedVenueIds.has(venueProductId)) return false
    const productId = asMatchId(product?.product)
    if (productId && countedProductIds.has(productId)) return false
    const key = normalizeTitleSupplier(product?.title, product?.supplierTitle)
    if (key !== '|' && countedKeys.has(key)) return false
    return true
  })
}

export const stockTakeParInputTestId = line =>
  `stock-take-par-${asMatchId(line?._id || line?.lineId || line?.title)}`

export const isStockTakeParInputTestId = value => {
  const id = String(value || '')
  if (!id.startsWith('stock-take-par-')) return false
  if (id === 'stock-take-par-usage') return false
  if (id.startsWith('stock-take-par-readiness')) return false
  if (id === 'stock-take-par-focus-row') return false
  return true
}

export const pickFirstCountedLineWithoutPar = ({
  skipped = [],
  products = [],
} = {}) => {
  const skippedList = Array.isArray(skipped) ? skipped : []
  const productsList = Array.isArray(products) ? products : []
  const noPar = skippedList.find(item => item?.reason === NO_PAR_REASON)
  if (!noPar) {
    return productsList.find(stockInStockWasEntered) || null
  }

  const lineId = asMatchId(noPar.lineId || noPar._id)
  const productId = asMatchId(noPar.productId)
  const title = String(noPar.title || '').trim()

  return (
    productsList.find(line => asMatchId(line?._id) === lineId) ||
    productsList.find(line => asMatchId(line?.productId) === productId) ||
    productsList.find(line => String(line?.title || '').trim() === title) ||
    productsList.find(stockInStockWasEntered) ||
    noPar
  )
}

export const focusStockTakeParInput = line => {
  if (typeof document === 'undefined' || !line) return
  const testId = stockTakeParInputTestId(line)
  const run = () => {
    const exact = [...document.querySelectorAll(`[data-testid="${testId}"]`)]
    const prefixed = [
      ...document.querySelectorAll('[data-testid^="stock-take-par-"]'),
    ].filter(el => isStockTakeParInputTestId(el.getAttribute('data-testid')))
    const unique = [...new Set(exact.length ? exact : prefixed)]
    const isVisibleHost = el => {
      const input = el?.tagName === 'INPUT' ? el : el?.querySelector?.('input')
      if (!input) return false
      const style =
        typeof window !== 'undefined' ? window.getComputedStyle(input) : null
      const rect = el.getBoundingClientRect()
      if (rect.width <= 0 || rect.height <= 0) return false
      if (
        style &&
        (style.visibility === 'hidden' || style.display === 'none')
      ) {
        return false
      }
      return true
    }
    const host = unique.find(isVisibleHost) || unique[0]
    const input =
      host?.tagName === 'INPUT' ? host : host?.querySelector?.('input') || host
    const row = host?.closest?.('tr')
    if (row) {
      row.classList.add(STOCK_TAKE_PAR_FOCUS_ROW_CLASS)
      row.scrollIntoView({ block: 'center', inline: 'nearest' })
    }
    input?.scrollIntoView?.({ block: 'center', inline: 'nearest' })
    if (typeof input?.focus === 'function') input.focus()
    if (typeof input?.select === 'function') input.select()
  }
  setTimeout(run, 0)
}

export const orderToParSuggestionKey = item =>
  String(item?.lineId || item?.productId || item?._id || '').trim()

export const orderToParGap = item => {
  const required = Number(item?.requiredQuantity)
  if (Number.isFinite(required) && required > 0) return required
  return Number(item?.suggestedOrder) || 0
}

export const sortOrderToParBiggestGapFirst = (items = []) =>
  [...(Array.isArray(items) ? items : [])].sort((left, right) => {
    const gapDiff = orderToParGap(right) - orderToParGap(left)
    if (gapDiff !== 0) return gapDiff
    return String(left?.title || '').localeCompare(String(right?.title || ''))
  })

export const ORDER_TO_PAR_SAVE_BEFORE_BASKET =
  'Save changes before adding these to the basket. This list is a live preview from the counts and pars on this page.'

export const ORDER_TO_PAR_PREVIEW_TOTAL_HINT = 'live preview · save to confirm'

/** Basket + still-due qty from the last Express OTP payload, keyed by productId. */
export const coverageMapsFromOrderToParItems = (items = []) => {
  const alreadyInBasket = {}
  const alreadyOnOrder = {}
  ;(Array.isArray(items) ? items : []).forEach(item => {
    const productId = asMatchId(item?.productId)
    if (!productId) return
    const inBasket = Number(item?.alreadyInBasket) || 0
    const onOrder = Number(item?.alreadyOnOrder) || 0
    if (inBasket > 0) {
      alreadyInBasket[productId] = Math.max(
        alreadyInBasket[productId] || 0,
        inBasket,
      )
    }
    if (onOrder > 0) {
      alreadyOnOrder[productId] = Math.max(
        alreadyOnOrder[productId] || 0,
        onOrder,
      )
    }
  })
  return { alreadyInBasket, alreadyOnOrder }
}

const coverageQtyForProduct = (coverage, productId, field) => {
  const id = asMatchId(productId)
  if (!id || !coverage) return 0
  const bag = coverage[field]
  if (!bag) return 0
  if (typeof bag.get === 'function') return Number(bag.get(id)) || 0
  return Number(bag[id]) || 0
}

/** Live below-par rows from the counts on screen, minus basket and still-due orders. */
export const previewOrderToParFromStockLines = ({
  lines = [],
  venueProducts = [],
  parEdits = {},
  coverage = {},
} = {}) => {
  const skipped = []
  const recommendations = []
  ;(Array.isArray(lines) ? lines : []).forEach(line => {
    const rawVenueProduct = findVenueProductForStockTakeProduct(
      line,
      venueProducts,
    )
    if (!stockInStockWasEntered(line)) {
      skipped.push({
        reason: NOT_COUNTED_REASON,
        title: line?.title,
        parLevel: Number(
          displayParLevelForStockLine(line, venueProducts, parEdits) || 0,
        ),
        lineId: line?._id,
      })
      return
    }
    if (line?.isLinkedStockGroup || line?.recipeId) {
      skipped.push({
        reason: 'merged_supplier_line',
        title: line?.title,
        parLevel: Number(
          displayParLevelForStockLine(line, venueProducts, parEdits) || 0,
        ),
        lineId: line?._id,
      })
      return
    }
    // Inactive shopping-list products cannot be ordered — never recommend them.
    if (rawVenueProduct?.isActive === false) {
      skipped.push({
        reason: 'inactive_shopping_list',
        title: line?.title,
        parLevel: Number(rawVenueProduct?.parLevel) || 0,
        lineId: line?._id,
        productId: line?.productId,
      })
      return
    }
    const parLevel = Number(
      displayParLevelForStockLine(line, venueProducts, parEdits) || 0,
    )
    if (!(parLevel > 0)) {
      skipped.push({
        reason: NO_PAR_REASON,
        title: line?.title,
        parLevel: 0,
        lineId: line?._id,
      })
      return
    }
    const currentStock = Number(line?.inStock) || 0
    const requiredQuantity = parLevel - currentStock
    if (!(requiredQuantity > 0)) {
      skipped.push({
        reason: 'at_or_above_par',
        title: line?.title,
        parLevel,
        lineId: line?._id,
      })
      return
    }
    const rawSuggested = Math.ceil(requiredQuantity - 1e-9)
    const alreadyInBasket = coverageQtyForProduct(
      coverage,
      line?.productId,
      'alreadyInBasket',
    )
    const alreadyOnOrder = coverageQtyForProduct(
      coverage,
      line?.productId,
      'alreadyOnOrder',
    )
    const suggestedOrder = Math.max(
      0,
      rawSuggested - alreadyInBasket - alreadyOnOrder,
    )
    if (!(suggestedOrder > 0)) {
      skipped.push({
        reason: 'already_covered',
        title: line?.title,
        parLevel,
        lineId: line?._id,
        productId: line?.productId,
        alreadyInBasket,
        alreadyOnOrder,
      })
      return
    }
    recommendations.push({
      lineId: String(line?._id || line?.lineId || ''),
      title: line?.title,
      productId: line?.productId,
      currentStock,
      parLevel,
      requiredQuantity,
      alreadyInBasket,
      alreadyOnOrder,
      suggestedOrder,
      estimatedValue: 0,
      needsShoppingList: !venueProductForStockTakeLine(line, venueProducts),
      isPreview: true,
    })
  })
  return {
    recommendations: sortOrderToParBiggestGapFirst(recommendations),
    skipped,
  }
}

export const estimatedOrderToParValue = (quantity, unitPrice) => {
  const qty = Number(quantity) || 0
  const price = Number(unitPrice) || 0
  return Math.round(qty * price * 100) / 100
}

/**
 * Remaining after basket and unreceipted open orders (already applied
 * on each recommendation's suggestedOrder / estimatedValue).
 */
export const summarizeOrderToParReview = (rows = []) => {
  const remaining = (Array.isArray(rows) ? rows : []).filter(
    item => (Number(item?.suggestedOrder) || 0) > 0,
  )
  const estimatedValue =
    Math.round(
      remaining.reduce(
        (sum, item) => sum + (Number(item?.estimatedValue) || 0),
        0,
      ) * 100,
    ) / 100
  return {
    belowParCount: remaining.length,
    estimatedValue,
  }
}

export const formatOrderToParLockSummary = ({
  belowParCount = 0,
  estimatedValueLabel = '£0.00',
} = {}) => {
  const products =
    belowParCount === 1
      ? '1 product below par'
      : `${belowParCount} products below par`
  return `${products} · ${estimatedValueLabel} remaining after basket and open orders`
}

export const formatOrderToParPackCopy = ({
  suggestedOrder,
  packSize,
  packLabel,
} = {}) => {
  const packs = Number(suggestedOrder) || 0
  const size = Number(packSize) || 0
  if (packs <= 0 || size <= 1) return null
  const raw = String(packLabel || '').trim()
  const label = !raw || /^pack of /i.test(raw) ? `case of ${size}` : raw
  return `Order ${packs} × ${label}`
}

export const formatOrderToParCoverageCopy = ({
  requiredQuantity,
  alreadyInBasket,
  alreadyOnOrder,
  suggestedOrder,
} = {}) => {
  const required = Number(requiredQuantity)
  const still = Number(suggestedOrder)
  const inBasket = Number(alreadyInBasket) || 0
  const onOrder = Number(alreadyOnOrder) || 0
  const need =
    Number.isFinite(required) && required > 0
      ? required
      : Number.isFinite(still)
      ? still
      : 0
  if (!(need > 0) && !(inBasket > 0) && !(onOrder > 0) && !(still > 0)) {
    return null
  }
  const stillNeed = Number.isFinite(still) ? still : 0
  const parts = [`Required ${need}`, `in basket ${inBasket}`]
  if (onOrder > 0) parts.push(`on order ${onOrder}`)
  parts.push(`still required ${stillNeed}`)
  return parts.join(' · ')
}

export const ORDER_TO_PAR_STRIP_REVIEW = 'Review'

/** Live take strip: recs get £ + count; empty states reuse 163 copy. */
export const formatOrderToParStripCopy = ({
  emptyMessage,
  belowParCount,
  estimatedValueLabel,
} = {}) => {
  if (emptyMessage) return emptyMessage
  const count = Number(belowParCount) || 0
  if (count <= 0) return null
  const value = String(estimatedValueLabel || '').trim()
  const about = value ? ` · about ${value}` : ''
  return `Below par on ${count}${about} · ${ORDER_TO_PAR_STRIP_REVIEW}.`
}

/**
 * Local review overlay. Does not mutate the stock-take lines.
 */
export const reviewOrderToParList = (
  recommendations = [],
  { removedKeys = [], qtyByKey = {} } = {},
) => {
  const removed = new Set(
    (Array.isArray(removedKeys) ? removedKeys : []).map(String),
  )
  const kept = (Array.isArray(recommendations) ? recommendations : []).filter(
    item => {
      const key = orderToParSuggestionKey(item)
      return key && !removed.has(key)
    },
  )

  return sortOrderToParBiggestGapFirst(kept).map(item => {
    const key = orderToParSuggestionKey(item)
    if (!Object.prototype.hasOwnProperty.call(qtyByKey, key)) return item
    const suggestedOrder = Math.max(0, Number(qtyByKey[key]) || 0)
    return {
      ...item,
      suggestedOrder,
      estimatedValue: estimatedOrderToParValue(
        suggestedOrder,
        item.currentPrice,
      ),
    }
  })
}

/** Body lines for add-to-basket. Removed rows are omitted; qty is the review overlay. */
export const buildOrderToParBasketReviewLines = (reviewRows = []) =>
  (Array.isArray(reviewRows) ? reviewRows : [])
    .filter(item => (Number(item?.suggestedOrder) || 0) > 0)
    .map(item => ({
      productId: item.productId,
      lineId: item.lineId || item._id,
      quantity: Number(item.suggestedOrder) || 0,
    }))

export const ORDER_TO_PAR_ASK_OLLIE_CHIP = 'Ask Ollie'
export const ORDER_TO_PAR_ASK_OLLIE_EMPTY = 'Why nothing to order?'
export const ORDER_TO_PAR_ASK_OLLIE_RECS =
  'What should I order after this stock take?'

export const formatOrderToParAskOllieSupplier = titles => {
  const names = [
    ...new Set(
      (Array.isArray(titles) ? titles : [])
        .map(title => String(title || '').trim())
        .filter(Boolean),
    ),
  ]
  if (!names.length) return ''
  if (names.length === 1) return names[0]
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names[0]} and ${names.length - 1} more`
}

export const formatOrderToParAskOllieChip = ({
  empty = false,
  recommendations = [],
  supplierTitles = [],
} = {}) => {
  const recs = empty
    ? []
    : (Array.isArray(recommendations) ? recommendations : []).filter(
        item => (Number(item?.suggestedOrder) || 0) > 0,
      )
  if (!recs.length) {
    return {
      label: ORDER_TO_PAR_ASK_OLLIE_CHIP,
      question: ORDER_TO_PAR_ASK_OLLIE_EMPTY,
    }
  }
  const supplier = formatOrderToParAskOllieSupplier(supplierTitles)
  const question = supplier
    ? `What should I order from ${supplier}?`
    : ORDER_TO_PAR_ASK_OLLIE_RECS
  return { label: ORDER_TO_PAR_ASK_OLLIE_CHIP, question }
}
